import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Custom element that renders a "Completa tu pedido" bundle: fetches
 * complementary (falling back to related) products via Shopify's Product
 * Recommendations JSON API, renders them as checkable cards, and adds every
 * checked item to the cart in a single request via one button.
 */
class CompleteOrderBundle extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-bundle-grid]');
    this.button = this.querySelector('[data-bundle-add-all]');
    this.totalEl = this.querySelector('[data-bundle-total]');
    this.checkboxes = [];

    this.button?.addEventListener('click', () => this.addAll());

    this.loadRecommendations();
  }

  async loadRecommendations() {
    const productId = this.dataset.productId;
    const maxProducts = this.dataset.maxProducts || '4';
    if (!productId) {
      this.hideSection();
      return;
    }

    try {
      let products = await this.fetchRecommendations(productId, maxProducts, 'complementary');
      if (!products.length) {
        products = await this.fetchRecommendations(productId, maxProducts, 'related');
      }
      if (!products.length) {
        this.hideSection();
        return;
      }
      this.renderProducts(products);
    } catch (error) {
      this.hideSection();
    }
  }

  hideSection() {
    const section = this.closest('.complete-order-section');
    if (section) section.setAttribute('hidden', 'true');
  }

  async fetchRecommendations(productId, limit, intent) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const base = root.endsWith('/') ? root : `${root}/`;
    const url = `${base}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=${encodeURIComponent(limit)}&intent=${intent}`;
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) return [];
    const data = await response.json();
    return (data.products || []).filter((product) => product.available);
  }

  resizeImage(url, width) {
    if (!url) return '';
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}width=${width}`;
  }

  renderProducts(products) {
    if (!this.grid) return;
    this.grid.innerHTML = '';

    products.forEach((product) => {
      const variant = product.variants && product.variants.find((v) => v.available);
      if (!variant) return;

      const price = (variant.price / 100).toFixed(2).replace('.', ',');
      const compareAt = variant.compare_at_price ? variant.compare_at_price / 100 : 0;
      const hasDiscount = compareAt > variant.price / 100;
      const comparePrice = hasDiscount ? compareAt.toFixed(2).replace('.', ',') : null;
      const imageSrc = this.resizeImage(product.featured_image || (product.images && product.images[0]), 300);
      const title = this.truncate(product.title, 50);

      const label = document.createElement('label');
      label.className = 'complete-order__item';

      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.className = 'complete-order__checkbox';
      checkbox.dataset.bundleItem = '';
      checkbox.dataset.variantId = String(variant.id);
      checkbox.dataset.price = String(variant.price / 100);
      checkbox.checked = true;
      checkbox.setAttribute('aria-label', product.title || '');

      const card = document.createElement('span');
      card.className = 'complete-order__card';

      if (hasDiscount) {
        const badge = document.createElement('span');
        badge.className = 'complete-order__badge';
        badge.textContent = 'Oferta';
        card.appendChild(badge);
      }

      const imageWrap = document.createElement('span');
      imageWrap.className = 'complete-order__image-wrap';
      if (imageSrc) {
        const img = document.createElement('img');
        img.src = imageSrc;
        img.alt = product.title || '';
        img.loading = 'lazy';
        img.width = 300;
        img.height = 300;
        imageWrap.appendChild(img);
      }
      card.appendChild(imageWrap);

      const titleEl = document.createElement('span');
      titleEl.className = 'complete-order__title';
      titleEl.textContent = title;
      card.appendChild(titleEl);

      const priceEl = document.createElement('span');
      priceEl.className = 'complete-order__price';
      priceEl.append(`${price} €`);
      if (comparePrice) {
        const compareEl = document.createElement('s');
        compareEl.className = 'complete-order__compare';
        compareEl.textContent = `${comparePrice} €`;
        priceEl.appendChild(document.createTextNode(' '));
        priceEl.appendChild(compareEl);
      }
      card.appendChild(priceEl);

      label.appendChild(checkbox);
      label.appendChild(card);
      this.grid.appendChild(label);
    });

    this.checkboxes = Array.from(this.grid.querySelectorAll('[data-bundle-item]'));
    this.checkboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', () => this.updateTotal());
    });

    if (this.button) this.button.disabled = false;
    this.updateTotal();
  }

  truncate(str, length) {
    if (!str) return '';
    return str.length > length ? `${str.slice(0, length - 1)}…` : str;
  }

  updateTotal() {
    let total = 0;
    let count = 0;

    this.checkboxes.forEach((checkbox) => {
      if (checkbox.checked) {
        total += parseFloat(checkbox.dataset.price || '0');
        count += 1;
      }
    });

    if (this.totalEl) {
      if (count === 0) {
        this.totalEl.textContent = this.dataset.emptyLabel || 'Selecciona al menos un producto';
      } else {
        const formattedTotal = total.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const label = this.dataset.addLabel || 'Añadir';
        const itemWord = count === 1 ? this.dataset.itemSingular || 'producto' : this.dataset.itemPlural || 'productos';
        this.totalEl.textContent = `${label} ${count} ${itemWord} · ${formattedTotal} €`;
      }
    }

    if (this.button) {
      this.button.disabled = count === 0;
    }
  }

  addAll() {
    const items = this.checkboxes
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => ({ id: Number(checkbox.dataset.variantId), quantity: 1 }));

    if (items.length === 0 || !this.button) return;

    this.button.disabled = true;
    this.button.setAttribute('data-loading', 'true');

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    cartItemsComponents.forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });

    const cartAddUrl =
      (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items, sections: sectionIds.join(',') }),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'No se pudo añadir al carrito',
              code: 'INVALID',
              detail: { description: response.description, errors: response.errors },
            })
          );
          deferred.reject(new Error(response.message || 'Add to cart failed'));
          return;
        }

        const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) =>
          r.json()
        );

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartResponse),
          detail: {
            items: cartResponse.items,
            source: 'complete-order-bundle',
            sourceId: this.id || 'complete-order-bundle',
            itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
            sections: response.sections,
            didError: false,
          },
        });
      })
      .catch((error) => {
        deferred.reject(error);
        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Error de red al añadir al carrito',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        if (this.button) {
          this.button.disabled = false;
          this.button.removeAttribute('data-loading');
        }
      });
  }
}

if (!customElements.get('complete-order-bundle')) {
  customElements.define('complete-order-bundle', CompleteOrderBundle);
}
